import React, { useContext, useEffect, useState } from 'react'
import SummaryApi from '../common'
import Context from '../context'
import displayINRCurrency from '../helpers/displayCurrency'
import { MdDelete } from "react-icons/md"
import { loadStripe } from '@stripe/stripe-js'
import { useNavigate } from 'react-router-dom' // For navigation

const Cart = () => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const context = useContext(Context)
    const loadingCart = new Array(4).fill(null)
    const navigate = useNavigate() // To handle page navigation

    // Form state
    const [userInfo, setUserInfo] = useState({
        name: '',
        phone: '',
        address: '',
        pincode: ''
    })

    // Payment type state
    const [paymentType, setPaymentType] = useState('online')

    const fetchData = async () => {
        const response = await fetch(SummaryApi.addToCartProductView.url, {
            method: SummaryApi.addToCartProductView.method,
            credentials: 'include',
            headers: {
                "content-type": 'application/json'
            },
        })
        const responseData = await response.json()
        if (responseData.success) {
            setData(responseData.data)
        }
    }

    const handleLoading = async () => {
        await fetchData()
    }

    useEffect(() => {
        setLoading(true)
        handleLoading()
        setLoading(false)
    }, [])

    const increaseQty = async (id, qty) => {
        const response = await fetch(SummaryApi.updateCartProduct.url, {
            method: SummaryApi.updateCartProduct.method,
            credentials: 'include',
            headers: {
                "content-type": 'application/json'
            },
            body: JSON.stringify({
                _id: id,
                quantity: qty + 1
            })
        })
        const responseData = await response.json()
        if (responseData.success) {
            fetchData()
        }
    }

    const decraseQty = async (id, qty) => {
        if (qty >= 2) {
            const response = await fetch(SummaryApi.updateCartProduct.url, {
                method: SummaryApi.updateCartProduct.method,
                credentials: 'include',
                headers: {
                    "content-type": 'application/json'
                },
                body: JSON.stringify({
                    _id: id,
                    quantity: qty - 1
                })
            })
            const responseData = await response.json()
            if (responseData.success) {
                fetchData()
            }
        }
    }

    const deleteCartProduct = async (id) => {
        const response = await fetch(SummaryApi.deleteCartProduct.url, {
            method: SummaryApi.deleteCartProduct.method,
            credentials: 'include',
            headers: {
                "content-type": 'application/json'
            },
            body: JSON.stringify({
                _id: id,
            })
        })
        const responseData = await response.json()
        if (responseData.success) {
            fetchData()
            context.fetchUserAddToCart()
        }
    }

    const handlePayment = async () => {
        if (paymentType === 'offline') {
            // Navigate to success page for offline payment
            navigate('/success')
        } else {
            // Stripe payment for online payment
            const stripePromise = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
            const response = await fetch(SummaryApi.payment.url, {
                method: SummaryApi.payment.method,
                credentials: 'include',
                headers: {
                    "content-type": 'application/json'
                },
                body: JSON.stringify({
                    cartItems: data
                })
            })
            const responseData = await response.json()
            if (responseData?.id) {
                stripePromise.redirectToCheckout({ sessionId: responseData.id })
            }
        }
    }

    const totalQty = data.reduce((previousValue, currentValue) => previousValue + currentValue.quantity, 0)
    const totalPrice = data.reduce((prev, curr) => prev + (curr.quantity * curr?.productId?.sellingPrice), 0)

    return (
        <div className='container mx-auto'>
            <div className='text-center text-lg my-3'>
                {data.length === 0 && !loading && <p className='bg-white py-5'>No Data</p>}
            </div>

            <div className='flex flex-col lg:flex-row gap-10 lg:justify-between p-4'>
                {/* View products */}
                <div className='w-full max-w-3xl'>
                    {loading ? (
                        loadingCart.map((el, index) => (
                            <div key={index} className='w-full bg-slate-200 h-32 my-2 border border-slate-300 animate-pulse rounded'></div>
                        ))
                    ) : (
                        data.map((product, index) => (
                            <div key={product?._id} className='w-full bg-white h-32 my-2 border border-slate-300 rounded grid grid-cols-[128px,1fr]'>
                                <div className='w-32 h-32 bg-slate-200'>
                                    <img src={product?.productId?.productImage[0]} className='w-full h-full object-scale-down mix-blend-multiply' alt="product" />
                                </div>
                                <div className='px-4 py-2 relative'>
                                    <div className='absolute right-0 text-blue-600 rounded-full p-2 hover:bg-blue-600 hover:text-white cursor-pointer' onClick={() => deleteCartProduct(product?._id)}>
                                        <MdDelete />
                                    </div>
                                    <h2 className='text-lg lg:text-xl'>{product?.productId?.productName}</h2>
                                    <p className='capitalize text-slate-500'>{product?.productId.category}</p>
                                    <div className='flex items-center justify-between'>
                                        <p className='text-red-600 font-medium text-lg'>{displayINRCurrency(product?.productId?.sellingPrice)}</p>
                                        <p className='text-slate-600 font-semibold text-lg'>{displayINRCurrency(product?.productId?.sellingPrice * product?.quantity)}</p>
                                    </div>
                                    <div className='flex items-center gap-3 mt-1'>
                                        <button className='border border-red-600 text-red-600 hover:bg-red-600 hover:text-white w-6 h-6 flex justify-center items-center rounded' onClick={() => decraseQty(product?._id, product?.quantity)}>-</button>
                                        <span>{product?.quantity}</span>
                                        <button className='border border-red-600 text-red-600 hover:bg-red-600 hover:text-white w-6 h-6 flex justify-center items-center rounded' onClick={() => increaseQty(product?._id, product?.quantity)}>+</button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Shipping Information Form */}
                <div className='w-full max-w-sm lg:-ml-10'> {/* Moved form to left with margin */}
                    <h2 className='text-xl font-semibold mb-4'>Shipping Information</h2>
                    <form className='bg-white p-4 rounded'>
                        <div className='mb-4'>
                            <label className='block text-sm font-medium'>Name</label>
                            <input type='text' className='border border-gray-300 p-2 w-full rounded' value={userInfo.name} onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })} />
                        </div>
                        <div className='mb-4'>
                            <label className='block text-sm font-medium'>Phone</label>
                            <input type='text' className='border border-gray-300 p-2 w-full rounded' value={userInfo.phone} onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })} />
                        </div>
                        <div className='mb-4'>
                            <label className='block text-sm font-medium'>Address</label>
                            <input type='text' className='border border-gray-300 p-2 w-full rounded' value={userInfo.address} onChange={(e) => setUserInfo({ ...userInfo, address: e.target.value })} />
                        </div>
                        <div className='mb-4'>
                            <label className='block text-sm font-medium'>Pincode</label>
                            <input type='text' className='border border-gray-300 p-2 w-full rounded' value={userInfo.pincode} onChange={(e) => setUserInfo({ ...userInfo, pincode: e.target.value })} />
                        </div>
                    </form>

                    {/* Payment Type Dropdown */}
                    <div className='mt-4'>
                        <label className='block text-sm font-medium mb-2'>Payment Method</label>
                        <select className='border border-gray-300 p-2 w-full rounded' value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                            <option value='online'>Online Payment</option>
                            <option value='offline'>Offline Payment</option>
                        </select>
                    </div>

                    {/* Summary section */}
                    {data.length > 0 && (
                        <div className='mt-5'>
                            {loading ? (
                                <div className='h-36 bg-slate-200 border border-slate-300 animate-pulse'></div>
                            ) : (
                                <div className='h-36 bg-white'>
                                    <h2 className='text-white bg-cyan-500 px-4 py-1'>Summary</h2>
                                    <div className='flex items-center justify-between px-4 gap-2 font-medium text-lg text-slate-600'>
                                        <p>Quantity</p>
                                        <p>{totalQty}</p>
                                    </div>
                                    <div className='flex items-center justify-between px-4 gap-2 font-medium text-lg text-slate-600'>
                                        <p>Total Price</p>
                                        <p>{displayINRCurrency(totalPrice)}</p>
                                    </div>
                                    <button className='bg-blue-600 p-2 text-white w-full mt-2' onClick={handlePayment}>Proceed to Payment</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Cart
